import { Button, LoadingSpinner, TermsOfService as ToS, PageAlert } from "@xapp/components";

import React, { FC, useCallback, useEffect, useState } from "react";
import { OnboardingProps } from "../models";
import { ClientApp } from "models";
import { IntegrationList } from "components/Integrations";
import { Product } from "utils/getProducts";

import classes from "./style.scss";

export interface TermsOfService {
    content: { rendered: string };
    link: string;
    modified_gmt: string;
    accepted?: boolean;
}

export interface CheckoutStepProps {
    /**
     * Error message to display to the user if something goes wrong.
     */
    error?: { error: string, message: string };
    /**
     * If we are gathering information for checkout
     */
    loading?: boolean;
    /**
     * Message to display while loading
     */
    loadingMessage?: string;
    /**
     * Optional style applied to the root div
     */
    style?: React.CSSProperties;
    /**
     * Optional class name to apply to the root div
     */
    className?: string;
    /**
     * App that the subscription will be applied to
     */
    app: Pick<ClientApp, "name" | "appId" | "organizationId" | "website" | "places" | "leadsContact">;
    /**
     * Properties to create the app
     */
    createProps: OnboardingProps;
    /**
     * Selected product
     */
    product?: Product;
    /**
     * Disable trial period and charge immediately
     */
    noTrial?: boolean;
    /**
     * Does the current tier support integrations
     */
    integrationsSupported?: boolean;
    /**
     * Terms of Service the user may or may not have already accepted
     */
    termsOfService?: TermsOfService;
    /**
     * User has requested to clear the coupon, typically because of error.
     */
    onClearCoupon?(): void;
    /**
     * User has accepted the terms of service
     */
    onAcceptTermsOfService(): void;
    /**
     * User requests to checkout
     */
    onCheckout(): void;
    /**
     * User has requested the next step
     */
    onNextStep?(): void;
}

/**
 * Display terms and checkout (optional)
 */
export const CheckoutStep: FC<CheckoutStepProps> = props => {

    const {
        app,
        createProps,
        error,
        loading,
        loadingMessage,
        style,
        termsOfService,
        className,
        onAcceptTermsOfService,
        onCheckout,
        product,
        noTrial,
        integrationsSupported
    } = props;

    const [tosAccepted, setTosAccepted] = useState<boolean>(false);

    useEffect(() => {
        setTosAccepted(termsOfService?.accepted);
    }, [termsOfService?.accepted]);

    const getContent = useCallback(() => {
        // if terms of service not accepted
        if (!tosAccepted) {
            return (
                <ToS
                    terms={termsOfService?.content?.rendered}
                    onAccept={onAcceptTermsOfService}
                />
            );
        } else {

            const website = app.website || createProps.website;
            const leadsEmail = app.leadsContact || createProps.leadsContact;
            const address = app.places?.length ? app.places[0].formattedAddress : createProps?.place?.formattedAddress; //?.length ? createProps.places[0].formattedAddress : "";

            return (
                <div className={classes.checkoutWrapper}>
                    <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "baseline" }}>
                        <h2>{product?.name}</h2>
                        <div style={{ display: "flex", flexDirection: "column" }}>
                            {noTrial ? <h4>{product?.price}</h4> : <h4>30 Days Free then {product?.price}</h4>}

                            <small>* Note! Coupons will be applied on the next final checkout page.</small>
                            {noTrial ? <small>** ${product?.priceInCents / 100} due today</small> : <></>}

                        </div>
                    </div>

                    <h3>Business Details</h3>
                    <dl>
                        <dt>Business Name</dt>
                        <dd>{app.name}</dd>

                        <dt>Business Address</dt>
                        <dd>{address}</dd>

                        <dt>Leads Email</dt>
                        <dd>{leadsEmail || (<i>Not Set</i>)}</dd>

                        <dt>Business Website</dt>
                        <dd>{website || (<i>Not Set</i>)}</dd>
                    </dl>

                    <h3>Features</h3>
                    <ul>
                        {/** Depending on if they gave us a website or not, we */}
                        <li>
                            <b>Reserve with Google</b>
                            <i>Expect your Book Online button to appear between 24 to 48 hours.  We will reach out with any issues.</i>
                        </li>
                        <li>
                            <b>Website Chat</b>
                            <i>Available for immediate install</i>
                        </li>
                        <li>
                            <b>AI Contact Capture</b>
                            <i>Available for immediate install</i>
                        </li>

                        {integrationsSupported ? (
                            <li>
                                <b>Integration</b>
                                <i>Integration will be setup after trial starts.</i>
                                <div>
                                    <IntegrationList onboardingMode={true} supportsIntegrations={true} app={app} disabled={true} limitIntegrations={createProps?.beta?.onboarding?.leadsIntegration} />
                                </div>
                            </li>
                        ) : (
                            <li>
                                <b>Email</b>
                                <i>Booking, Leads & Contact Requests will be sent to your leads email (listed above)</i>
                            </li>
                        )}
                    </ul>

                    <div style={{ padding: "20px", rowGap: "2px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                        {/* TODO: Add button up top */}
                        <Button
                            id="stepEnterPayment"
                            leftIcon="verified"
                            label={`Enter Payment Information`}
                            variation="primary"
                            onClick={onCheckout}
                        />
                        {noTrial ? (
                            <small>Payment information is required to start the subscription.</small>
                        ) : (
                            <>
                                <small>Payment information is required to start the trial.</small>
                                <small>You will not be billed today but after the 30 day free trial.</small>
                            </>
                        )}

                    </div>
                </div >
            );
        }
    }, [tosAccepted, termsOfService?.content?.rendered, onAcceptTermsOfService, app, createProps.website, createProps.leadsContact, createProps?.place?.formattedAddress, createProps?.beta?.onboarding?.leadsIntegration, product?.name, product?.price, product?.priceInCents, noTrial, integrationsSupported, onCheckout])

    return (
        <div style={{ ...style }} className={className}>
            {error ? (
                <PageAlert background="orange" >
                    <p>{error.message}</p>
                    <pre style={{ overflow: "scroll" }}>{error.error}</pre>
                    {/**We need a call to action for them to get help */}
                </PageAlert>
            ) : (<></>)}
            {loading ? (
                <LoadingSpinner loadingText={loadingMessage} />
            ) : (getContent())}
        </div >
    );
}